import React, { useContext, useEffect, useState } from 'react';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import { HeaderType } from '../../components/Header/types/Header';
import { FooterType } from '../../components/Footer/types/Footer';
import ParkingProjectCard from '../../components/ParkingProjectCard/ParkingProjectCard';
import { Helmet } from 'react-helmet';
import styles from './ParkingPage.module.css'
import Loader from '../../components/Loader/Loader';
import { LoaderContext } from '../../components/Loader/LoaderContext';
import api from '../../utils/api';
import { transformParkingProjectFeatures, transformParkings } from '../../utils/transformers';

interface Project {
  project: number;
  title: string;
  description: string;
  preview: string;
  features: {
    id: number;
    text: string;
    image?: string;
  }[];
  image?: string;
  parkingCount: number;
  minPrice?: number;
}

export default function ParkingPage() {
  const [projectsData, setProjectsData] = useState<Project[]>([]);
  const { isInitialLoad, setIsInitialLoad } = useContext(LoaderContext);

  useEffect(() => {
    document.documentElement.classList.add('black');
    document.body.classList.add('black');

    return () => {
      document.documentElement.classList.remove('black');
      document.body.classList.remove('black');
    };
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setIsInitialLoad(true);
      try {
        const parkingProjectFeaturesResponse = await api.parking.getProjectFeatures();
        const parkingsResponse = await api.parking.getAll();
        
        const parkingProjectFeatures = transformParkingProjectFeatures(parkingProjectFeaturesResponse);
        const parkings = transformParkings(parkingsResponse);

        const projects: Project[] = parkingProjectFeatures
          .filter((project) => project.project !== undefined && project.project !== null)
          .map((project) => {
            const availableParkings = parkings.filter((parking) => parking.available === true);
            const parkingCount = availableParkings.length;
            let minPrice: number | undefined = undefined;
            
            if (availableParkings.length > 0) {
              const prices = availableParkings.map((parking) => {
                const price = typeof parking.price === 'string' ? parseFloat(parking.price.replace(/ /g, '')) : parking.price;
                const area = typeof parking.area === 'string' ? parseFloat(parking.area.replace(/ /g, '')) : parking.area;
                const discountPerSqm = parking.discountProperty ?? 0;
                const totalDiscount = discountPerSqm * area;
                let finalPrice = price - totalDiscount;
                if (finalPrice < 0) finalPrice = 0;
                return finalPrice;
              });
              const filteredPrices = prices.filter((price: number) => price > 0);
              minPrice = filteredPrices.length > 0 ? Math.min(...filteredPrices) : undefined;
            }
            
            let features = Array.isArray(project.features) ? [...project.features] : [];
            if (minPrice !== undefined) {
              const formattedPrice = minPrice.toLocaleString('ru-RU');
              features.push({
                id: features.length + 1,
                text: `От ${formattedPrice} ₽`,
                image: undefined,
              });
            }
            
            return {
              project: project.project,
              title: project.title,
              description: project.description,
              preview: project.preview,
              features,
              image: project.image,
              parkingCount,
              minPrice,
            };
          });

        setProjectsData(projects);
      } catch (error) {
        console.error('Ошибка при загрузке данных:', error);
        setProjectsData([]);
      } finally {
        setIsInitialLoad(false);
      }
    };

    fetchData();
  }, [setIsInitialLoad]);

  return (
    <div className={styles.page}>
      <Helmet>
        <title>Машино-места | Восток Строй</title>
        <meta name="author" content="Восток Строй" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://vostokstroy.com/projects" />
        <meta property="og:image" content="https://vostokstroy.com/open-graphs/main-opengraph.jpg" />
        <meta property="og:url" content="https://vostokstroy.com/projects" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Восток Строй" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://vostokstroy.com/open-graphs/main-opengraph.jpg" />
        <link rel="icon" type="image/svg+xml" href="/favicons/main-fav-32x32.svg" />
        <link rel="icon" type="image/png" sizes="16x16" href="/favicons/main-fav-16x16.png" />
        <link rel="icon" type="image/png" sizes="32x32" href="/favicons/main-fav-32x32.png" />
        <link rel="apple-touch-icon" sizes="180x180" href="/favicons/main-apple-touch-icon-180x180.png" />
        <link rel="icon" type="image/png" sizes="180x180" href="/favicons/main-fav-180x180.png" />
      </Helmet>
      {isInitialLoad ? (
        <Loader />
      ) : (
        <>
          <Header theme={HeaderType.Dark} />
          <div>
            {projectsData.filter(project => project.project).map((project, index) => (
              <ParkingProjectCard
                key={project.project}
                project={project.project}
                title={project.title}
                description={project.description}
                features={project.features}
                image={project.image}
                preview={project.preview}
                paddingVariant={index === 0 ? 'large' : 'default'}
                parkingCount={project.parkingCount}
                minPrice={project.minPrice}
              />
            ))}
          </div>
          <Footer theme={FooterType.Dark} />
        </>
      )}
    </div>
  );
}